import { type ReportAnalyticsDaily, rate } from "@skud/domain";

/**
 * Analitika seriyalari ustidagi sof hosilalar (spec 2026-09-17, reja
 * E2). React va vaqtga bog'lanmaydi.
 */

/**
 * Orqaga qaragan sirpanuvchi o'rtacha: `i` — `[i - window + 1, i]`.
 *
 * ⚠️ Boshidagi nuqtalar QISQA oyna bilan hisoblanadi (`null` EMAS) —
 * `ColumnChart.average.values` ustunlar bilan indeksma-indeks mos
 * bo'lishi shart.
 *
 * ⚠️ Oyna NUQTALAR bo'yicha, kalendar kunlari bo'yicha EMAS: `daily`
 * faqat SQL qaytargan kunlarni oladi (bo'sh kun qo'shilmaydi), ya'ni
 * «7 kunlik» — so'nggi 7 ta MA'LUMOTLI kun.
 */
export function rollingAverage(values: readonly number[], window: number): number[] {
	const oyna = Math.max(1, Math.trunc(window));

	return values.map((_, i) => {
		const bolak = values.slice(Math.max(0, i - oyna + 1), i + 1);
		return bolak.reduce((a, b) => a + b, 0) / bolak.length;
	});
}

export interface WeeklyShare {
	/** Haftaning dushanbasi, `YYYY-MM-DD`. */
	weekStart: string;
	primary: number;
	flagged: number;
	/** `flagged / primary` bir kasrli foiz; maxraj nol → `null`. */
	share: number | null;
}

const KUN_MS = 86_400_000;

/**
 * ⚠️ **Hafta DUSHANBADAN** (ISO) — `period.ts` dagi `presetRange`
 * bilan bir xil. UTC sana arifmetikasi: `YYYY-MM-DD` lokal sana
 * bo'lib keladi, soat mintaqasi aralashmasin.
 */
function haftaBoshi(sana: string): string {
	const [y, m, d] = sana.split("-").map(Number);
	const kun = new Date(Date.UTC(y as number, (m as number) - 1, d as number));
	const isoOffset = (kun.getUTCDay() + 6) % 7;
	return new Date(kun.getTime() - isoOffset * KUN_MS).toISOString().slice(0, 10);
}

/**
 * Kunlik qatorlarni ISO haftalarga yig'adi va `flagged` ulushini
 * hisoblaydi (`zone_exits` da — ruxsatsiz chiqishlar ulushi).
 *
 * ⚠️ Ulush `@skud/domain` ning `rate()` idan — nolga bo'lish
 * qorovuli bitta joyda.
 */
export function weeklyShare(daily: readonly ReportAnalyticsDaily[]): WeeklyShare[] {
	const haftalar = new Map<string, { primary: number; flagged: number }>();

	for (const kun of daily) {
		const kalit = haftaBoshi(kun.date);
		const joriy = haftalar.get(kalit) ?? { primary: 0, flagged: 0 };
		joriy.primary += kun.primary;
		joriy.flagged += kun.flagged;
		haftalar.set(kalit, joriy);
	}

	return [...haftalar.entries()]
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([weekStart, h]) => ({
			weekStart,
			primary: h.primary,
			flagged: h.flagged,
			share: rate(h.flagged, h.primary),
		}));
}

/** Ish soatlari — soatlik diagrammalarning standart oynasi. */
export const WORK_HOURS = { from: 8, to: 19 } as const;

/**
 * Soat o'qi oynasi: standart 08–19, ma'lumotli soat undan tashqarida
 * bo'lsa oyna o'sha soatgacha KENGAYADI (tungi chiqish yashirinmasin).
 *
 * ⚠️ Issiqlik xaritasi va soatlik chiziq SHU oynani birga ishlatadi —
 * ikki diagramma bir xil soatlarni ko'rsatadi.
 */
export function hourWindow(hours: readonly number[]): { from: number; to: number } {
	return {
		from: Math.min(WORK_HOURS.from, ...hours),
		to: Math.max(WORK_HOURS.to, ...hours),
	};
}
